<template>
  <v-card
    :loading="loading"
    class="pa-0"
  >
    <v-card-title>
      Mailing HQ questions
      <v-spacer />
      <v-btn-toggle
        v-model="listType"
        mandatory
        dense
        class="mr-2"
      >
        <v-btn
          small
          value="gaps"
        >
          Gaps
        </v-btn>
        <v-btn
          small
          value="all"
        >
          Everything
        </v-btn>
      </v-btn-toggle>
      <v-select
        v-model="days"
        :items="rangeOptions"
        label="Range"
        dense
        hide-details
        outlined
        style="max-width: 140px"
        class="mr-2"
      />
      <v-btn
        outlined
        @click="loadData"
      >
        Refresh
      </v-btn>
    </v-card-title>

    <v-card-subtitle>
      What people typed into the Ask box on <strong>/direct-mail</strong>, grouped by question. A
      question we could not answer is a <strong>guide worth writing</strong>, in the words readers
      actually used — better evidence than keyword research, because they were already on the hub.
    </v-card-subtitle>

    <v-alert
      v-if="error"
      type="error"
      dense
      text
      class="mx-4"
    >
      {{ error }}
    </v-alert>

    <v-card-text v-if="rows.length">
      <v-row dense>
        <v-col
          v-for="stat in stats"
          :key="stat.label"
          cols="6"
          md="3"
        >
          <div class="text-caption text--secondary text-uppercase">
            {{ stat.label }}
          </div>
          <div
            class="text-h5 font-weight-bold"
            :class="stat.class"
          >
            {{ stat.value }}
          </div>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-text class="pa-0">
      <!-- Rows arrive worst-answered first from the server, and that ordering is kept rather than
           sorting by volume: how often a question was asked is exactly what buries the ones we
           cannot answer under the ones we answer well. The columns are still sortable by hand. -->
      <v-data-table
        :items="visibleRows"
        :headers="headers"
        item-key="normalizedQuestion"
        :items-per-page="25"
        :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
        :loading="loading"
        no-data-text="Nothing logged in this window yet."
      >
        <template #[`item.question`]="{ item }">
          <div>{{ item.question }}</div>
          <!-- The review line. An answer nobody has read is a claim nobody has approved, and on a
               hub where every published figure carries a source that is not good enough. -->
          <div
            v-if="item.lastAnswer"
            class="text-caption text--secondary font-italic mt-1"
          >
            {{ item.lastAnswer }}
          </div>
        </template>

        <template #[`item.unanswered`]="{ item }">
          <span :class="item.unanswered > 0 ? 'error--text font-weight-bold' : ''">
            {{ item.unanswered }}
          </span>
        </template>

        <template #[`item.averageTopScore`]="{ item }">
          <!-- Comparable between rows in this table and nowhere else - it is a blended relevance,
               not a percentage. Amber marks a question we answer BADLY, which is a page to improve
               rather than a page to write. -->
          <span :class="isWeak(item.averageTopScore) ? 'warning--text' : ''">
            {{ fmtScore(item.averageTopScore) }}
          </span>
        </template>

        <template #[`item.clicked`]="{ item }">
          <span :class="item.asked > item.unanswered && item.clicked === 0 ? 'warning--text' : ''">
            {{ item.clicked }}
          </span>
        </template>

        <template #[`item.answered`]="{ item }">
          <v-chip
            v-if="item.answered > 0"
            x-small
            color="info"
          >
            {{ item.answered }}
          </v-chip>
          <span v-else>&mdash;</span>
        </template>

        <template #[`item.lastAsked`]="{ item }">
          {{ fmtDate(item.lastAsked) }}
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
// Content > Questions.
//
// One source: dbo.MailingHqQuestionLog, written as each question is asked - see
// Core/BAL/Content/MailingHq/QuestionLogService.cs. It sits in the Content area rather than
// anywhere else because it is a reading of the CONTENT PAGES one tab over: every row is either a
// guide that does not exist yet, or one that does and did not answer the question.
//
// Below this score the best hit cleared the server's relevance floor without being much of an
// answer. Declared once so the Gaps filter and the amber column cannot drift apart - they are the
// same judgement shown two ways.
const WEAK_SCORE = 0.35;

export default {
  name: 'ContentQuestions',
  data() {
    return {
      loading: false,
      error: null,
      rows: [],
      days: 30,
      listType: 'gaps',
      rangeOptions: [
        { text: 'Last 7 days', value: 7 },
        { text: 'Last 30 days', value: 30 },
        { text: 'Last 90 days', value: 90 },
        { text: 'Last year', value: 365 }
      ],
      headers: [
        { text: 'Question', value: 'question' },
        { text: 'Asked', value: 'asked', width: 100 },
        { text: 'Unanswered', value: 'unanswered', width: 130 },
        { text: 'Best score', value: 'averageTopScore', width: 120 },
        { text: 'Clicked', value: 'clicked', width: 110 },
        { text: 'Written', value: 'answered', width: 110 },
        { text: 'Last asked', value: 'lastAsked', width: 130 }
      ]
    };
  },
  computed: {
    visibleRows() {
      return this.listType === 'gaps' ? this.rows.filter(this.isGap) : this.rows;
    },
    stats() {
      const asked = this.rows.reduce((sum, r) => sum + r.asked, 0);
      const unanswered = this.rows.reduce((sum, r) => sum + r.unanswered, 0);
      const rate = asked === 0 ? 0 : Math.round((unanswered / asked) * 100);

      // Answered, but nobody opened anything. The relevance half of the problem: we had the copy
      // and it did not look like the answer, which is an edit rather than a new guide.
      const noClicks = this.rows.filter((r) => r.asked > r.unanswered && r.clicked === 0).length;

      return [
        { label: 'Questions asked', value: asked.toLocaleString() },
        { label: 'Distinct', value: this.rows.length.toLocaleString() },
        {
          label: 'Unanswered rate',
          value: `${rate}%`,
          class: rate > 10 ? 'error--text' : ''
        },
        {
          label: 'Answered, no clicks',
          value: noClicks.toLocaleString(),
          class: noClicks > 0 ? 'warning--text' : ''
        }
      ];
    }
  },
  watch: {
    days() {
      this.loadData();
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      this.error = null;
      try {
        this.rows = await this.$store.dispatch('content/getMailingHqQuestions', {
          days: this.days
        });
      } catch (e) {
        this.error = (e && e.message) || 'Failed to load Mailing HQ questions';
      } finally {
        this.loading = false;
      }
    },
    /** A question we answered weakly: something was shown, and it was not really the answer. */
    isWeak(score) {
      return score > 0 && score < WEAK_SCORE;
    },
    // A gap is a question we could not answer at all, or answered so weakly the reader got links
    // nobody would call an answer. Both are pages to write; the difference is only whether there
    // was anything to show in the meantime.
    isGap(row) {
      return row.unanswered > 0 || this.isWeak(row.averageTopScore);
    },
    fmtScore(value) {
      return value > 0 ? value.toFixed(2) : '—';
    },
    fmtDate(value) {
      return value ? new Date(value).toLocaleDateString() : '';
    }
  }
};
</script>
